import React, { Component } from 'react';
import CloseIcon from '@material-ui/icons/Close';
import {
  Typography,
  TextField,
  Grid,
  IconButton,
  MenuItem,
  Dialog,
  DialogContent,
  DialogTitle,
  Button,
  DialogActions,
  Switch,
  ExpansionPanel,
  ExpansionPanelDetails,
  ExpansionPanelSummary,
} from '@material-ui/core';
import {
  WithStyles, withStyles, createStyles, Theme,
} from '@material-ui/core/styles/index';
import { TypographyProps } from '@material-ui/core/Typography';
import WaxPaper from 'hcla-web-frontend-primitives/app/components/primitives/Paper/WaxPaper/WaxPaper';
import InputRules from '../Inputs/InputRules';
import { Configurations } from './Quantification';
import QuantifyTreeButton from './QuantifyTreeButton';
import { FullScreenConsumer } from '../../../../Primitives/FullScreenProvider';
import { updateConfigurations } from './Utils';

const styles = (theme: Theme) => createStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    flex: '1 0 auto',
  },
  action: {
    margin: `0 ${theme.spacing(2)}px`,
  },
  expansionPanelDetails: {
    display: 'flex',
    flexDirection: 'column',
  },
  expansionPanelRoot: {
    background: 'transparent',
  },
  switchForm: {
    display: 'flex',
    justifyContent: 'space-between',
    flexDirection: 'row',
    alignItems: 'center',
    flexGrow: 'inherit',
    paddingRight: '0 !important',
  },
});

interface Props extends WithStyles<typeof styles> {
  modelId: number;
  treeId?: number;
  treeType: string;
  open?: boolean;
  onClose(): void;
  editableConfig: { [key: string]: Configurations };
  currentKey: string;
  newConfig: boolean;
  quantifyOverride?: any;
}

interface State {
  settingsModalOpen: boolean;
  userConfigurationName: string;
  BBNSolver: number;
  BDDConstructor: number;
  OrderingRule: number;
  samplingMethod: string;
  sampleNumber: number;
  confidenceLevel: number;
  importanceEvents: string;
  importanceMeasures: string;
  selectImportanceMeasures: boolean;
  missionTime: number;
  replaceTransferGates: boolean;
  selectMissionTime: boolean;
  selectTTFCDF: boolean;
  selectUncertainty: boolean;
  user_defined_max_cutset: number;
}

interface DefaultConfig {
  userConfigurationName: string;
  BBNSolver: number;
  BDDConstructor: number;
  OrderingRule: number;
  samplingMethod: string;
  sampleNumber: number;
  confidenceLevel: number;
  importanceEvents: string;
  importanceMeasures: string;
  selectImportanceMeasures: boolean;
  selectMissionTime: boolean;
  missionTime: number;
  replaceTransferGates: boolean;
  selectTTFCDF: boolean;
  selectUncertainty: boolean;
  user_defined_max_cutset: number;
}

const getDefaultConfig = (): DefaultConfig => {
  return ({
    userConfigurationName: '',
    BBNSolver: 1000,
    BDDConstructor: 2001,
    OrderingRule: 3000,
    samplingMethod: '',
    sampleNumber: 100,
    confidenceLevel: 0.90,
    importanceEvents: '',
    importanceMeasures: '',
    selectImportanceMeasures: false,
    selectMissionTime: false,
    missionTime: null,
    replaceTransferGates: false,
    selectTTFCDF: false,
    selectUncertainty: false,
    user_defined_max_cutset: -1,
  });
};

function PaperComponent(props: any) {
  return (
    <WaxPaper {...props} multi />
  );
}

const ExpansionTitleTypography = (props: TypographyProps) => {
  return <Typography {...props} variant="h6" />;
};

class UserQuantificationSettings extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { ...getDefaultConfig(), settingsModalOpen: false };
  }

  componentDidMount(): void {
    this.editConfigurations(this.props);
  }

  componentWillReceiveProps(nextProps: Readonly<Props>, nextContext: any): void {
    this.editConfigurations(nextProps);
  }

  private handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    // @ts-ignore
    this.setState({ [e.target.name]: e.target.value });
  };

  private handleSwitchChange = (name: string, e: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ ...this.state, [name]: e.target.checked });
  };

  private editConfigurations = (props: { editableConfig: {[key: string]: Configurations}; currentKey: string }) => {
    const { editableConfig, currentKey } = props;
    let toSet = getDefaultConfig();
    if (editableConfig && currentKey && editableConfig[currentKey]) {
      toSet = {
        userConfigurationName: currentKey || '',
        BBNSolver: 1000,
        BDDConstructor: 2001,
        OrderingRule: 3000,
        samplingMethod: editableConfig[currentKey].configuration.quantify.sampling.method || '',
        sampleNumber: editableConfig[currentKey].configuration.quantify.sampling.number_of_samples || 10000,
        confidenceLevel: editableConfig[currentKey].configuration.quantify.sampling.confidence_level || 0.90,
        importanceEvents: 'all',
        importanceMeasures: 'all',
        missionTime: editableConfig[currentKey].configuration.quantify.mission_test_interval || null,
        replaceTransferGates: editableConfig[currentKey].configuration.constructor.replace_transfer_gates_with_basic_events,
        selectUncertainty: editableConfig[currentKey].configuration.quantify.type.includes('uncertainty'),
        selectMissionTime: editableConfig[currentKey].configuration.quantify.mission_test_interval !== null,
        selectImportanceMeasures: !InputRules.isObjectEmpty(editableConfig[currentKey].configuration.quantify.importance),
        selectTTFCDF: editableConfig[currentKey].configuration.quantify.type.includes('time_to_failure_cdf'),
        user_defined_max_cutset: editableConfig[currentKey].configuration.quantify.user_defined_max_cutset || -1,
      };
    }
    this.setState(prevState => ({
      ...prevState,
      ...toSet,
    }));
  };

  private setQuantificationConfigs = (): void => {
    const {
      samplingMethod,
      sampleNumber,
      confidenceLevel,
      missionTime,
      replaceTransferGates,
      selectUncertainty,
      selectMissionTime,
      selectImportanceMeasures,
      selectTTFCDF,
      userConfigurationName,
      user_defined_max_cutset,
    } = this.state;

    const params: Configurations = {
      configuration: {
        constructor: {
          replace_transfer_gates_with_basic_events: replaceTransferGates,
        },
        engine: {
          BBNSolver: 1000,
          BDDConstructor: 2001,
          OrderingRule: 3000,
        },
        quantify: {
          type: null,
          mission_test_interval: null,
          user_defined_max_cutset: Number(user_defined_max_cutset),
          targets: '__all__',
          sampling: {},
          importance: {},
        },
      },
    };

    if (selectTTFCDF) {
      params.configuration.quantify.type = 'time_to_failure_cdf';
      if (selectUncertainty) params.configuration.quantify.type += '_with_uncertainty';
    } else if (selectUncertainty) {
      params.configuration.quantify.type = 'uncertainty';
      if (selectMissionTime) params.configuration.quantify.type += '_at_fixed_time';
    } else {
      params.configuration.quantify.type = 'point_estimate';
      if (selectMissionTime) params.configuration.quantify.type += '_at_fixed_time';
    }

    if (selectUncertainty) {
      params.configuration.quantify.sampling = {
        method: samplingMethod,
        number_of_samples: Number(sampleNumber),
        confidence_level: Number(confidenceLevel),
      };
    }

    if (selectMissionTime) {
      params.configuration.quantify.mission_test_interval = Number(missionTime);
    }

    if (selectImportanceMeasures) {
      params.configuration.quantify.importance = {
        events: 'all',
        measures: [
          'all',
        ],
      };
    }

    updateConfigurations(userConfigurationName, params as Configurations);
  };

  render() {
    const {
      classes, open, onClose, modelId, treeId, treeType, quantifyOverride,
    } = this.props;

    const {
      userConfigurationName,
      samplingMethod,
      sampleNumber,
      confidenceLevel,
      missionTime,
      replaceTransferGates,
      selectUncertainty,
      selectMissionTime,
      selectImportanceMeasures,
      selectTTFCDF,
      user_defined_max_cutset,
    } = this.state;

    const userConfigNameError = InputRules.isEmpty(userConfigurationName);
    const missionTimeError = !InputRules.isPositive((missionTime || '').toString());
    const samplingMethodError = InputRules.isEmpty(samplingMethod);
    const sampleNumberError = !InputRules.isPositiveIntegerBetweenValues(sampleNumber.toString(), 1, 10000);
    const confidenceLevelError = !InputRules.isPositiveBetweenValues(confidenceLevel.toString());
    return (
      <FullScreenConsumer>
        {({ isFull }) => (
          <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            disableRestoreFocus
            disablePortal={isFull}
            PaperComponent={PaperComponent}
          >
            <DialogTitle>
              <Grid container alignItems="center" justify="space-between">
                <Grid item>
                  <Typography variant="h5">Create Quantification Configuration</Typography>
                </Grid>
                <Grid item>
                  <IconButton onClick={onClose}>
                    <CloseIcon />
                  </IconButton>
                </Grid>
              </Grid>
            </DialogTitle>
            <DialogContent>
              <TextField
                label="Configuration Name"
                name="userConfigurationName"
                value={userConfigurationName}
                onChange={this.handleChange}
                InputLabelProps={{ shrink: true }}
                margin="normal"
                fullWidth
                variant="outlined"
                error={userConfigNameError}
                helperText={userConfigNameError ? InputRules.DESCRIPTIONS.NAME : null}
              />
              <ExpansionPanel expanded={selectUncertainty} classes={{ root: classes.expansionPanelRoot }}>
                <ExpansionPanelSummary>
                  <div className={classes.switchForm}>
                    <ExpansionTitleTypography>Quantify Uncertainties</ExpansionTitleTypography>
                    <Switch
                      onChange={e => this.handleSwitchChange('selectUncertainty', e)}
                      value="selectUncertainty"
                      checked={selectUncertainty}
                    />
                  </div>
                </ExpansionPanelSummary>
                <ExpansionPanelDetails className={classes.expansionPanelDetails}>
                  <TextField
                    label="Select a sampling method"
                    name="samplingMethod"
                    value={samplingMethod}
                    onChange={this.handleChange}
                    InputLabelProps={{ shrink: true }}
                    margin="normal"
                    select
                    SelectProps={{
                      MenuProps: {
                        disablePortal: isFull,
                      },
                    }}
                    fullWidth
                    variant="outlined"
                    error={samplingMethodError}
                    helperText={samplingMethodError ? InputRules.DESCRIPTIONS.SAMPLING : null}
                  >
                    <MenuItem value="monte_carlo">Monte Carlo</MenuItem>
                    <MenuItem value="latin_hypercube_random">Latin Hypercube Random</MenuItem>
                    <MenuItem value="latin_hypercube_center">Latin Hypercube Center</MenuItem>
                    <MenuItem value="latin_hypercube_improved">Latin Hypercube Improved</MenuItem>
                    <MenuItem value="quasi_monte_carlo_halton">Quasi Monte Carlo Halton</MenuItem>
                    <MenuItem value="quasi_monte_carlo_faure">Quasi Monte Carlo Faure</MenuItem>
                    <MenuItem value="quasi_monte_carlo_hammersley">Quasi Monte Carlo Hammersley</MenuItem>
                    <MenuItem value="markov_chain_monte_carlo">Markov Chain Monte Carlo</MenuItem>
                    <MenuItem value="markov_chain_quasi_monte_carlo">Markov Chain Quasi Monte Carlo</MenuItem>
                  </TextField>
                  <TextField
                    type="number"
                    inputProps={{ min: 1, step: 1, max: 10000 }}
                    label="Select the number of samples"
                    name="sampleNumber"
                    value={sampleNumber}
                    onChange={this.handleChange}
                    InputLabelProps={{ shrink: true }}
                    margin="normal"
                    fullWidth
                    variant="outlined"
                    error={sampleNumberError}
                    helperText={sampleNumberError ? InputRules.DESCRIPTIONS.SAMPLE_NUMBER : null}
                  />
                </ExpansionPanelDetails>
              </ExpansionPanel>
              <ExpansionPanel disabled={selectTTFCDF} expanded={selectMissionTime} classes={{ root: classes.expansionPanelRoot }}>
                <ExpansionPanelSummary>
                  <div className={classes.switchForm}>
                    <ExpansionTitleTypography>Mission Time</ExpansionTitleTypography>
                    <Switch
                      onChange={e => this.handleSwitchChange('selectMissionTime', e)}
                      value="selectMissionTime"
                      checked={selectMissionTime}
                    />
                  </div>
                </ExpansionPanelSummary>
                <ExpansionPanelDetails className={classes.expansionPanelDetails}>
                  <TextField
                    type="number"
                    inputProps={{ min: 1, step: 1 }}
                    label="Select the mission time"
                    name="missionTime"
                    value={missionTime}
                    onChange={this.handleChange}
                    InputLabelProps={{ shrink: true }}
                    margin="normal"
                    fullWidth
                    variant="outlined"
                    error={missionTimeError}
                    helperText={missionTimeError ? InputRules.DESCRIPTIONS.MISSION_TIME : null}
                  />
                </ExpansionPanelDetails>
              </ExpansionPanel>
              <ExpansionPanel expanded={false} classes={{ root: classes.expansionPanelRoot }}>
                <ExpansionPanelSummary>
                  <div className={classes.switchForm}>
                    <ExpansionTitleTypography>Rank Importance Measures</ExpansionTitleTypography>
                    <Switch
                      onChange={e => this.handleSwitchChange('selectImportanceMeasures', e)}
                      value="selectImportanceMeasures"
                      checked={selectImportanceMeasures}
                    />
                  </div>
                </ExpansionPanelSummary>
              </ExpansionPanel>
              <ExpansionPanel expanded={false} disabled={selectMissionTime} classes={{ root: classes.expansionPanelRoot }}>
                <ExpansionPanelSummary>
                  <div className={classes.switchForm}>
                    <ExpansionTitleTypography>Compute Time To Failure CDF</ExpansionTitleTypography>
                    <Switch
                      onChange={e => this.handleSwitchChange('selectTTFCDF', e)}
                      value="selectTTFCDF"
                      checked={selectTTFCDF}
                    />
                  </div>
                </ExpansionPanelSummary>
              </ExpansionPanel>
              <ExpansionPanel expanded={false} classes={{ root: classes.expansionPanelRoot }}>
                <ExpansionPanelSummary>
                  <div className={classes.switchForm}>
                    <ExpansionTitleTypography>Replace Transfer Gates</ExpansionTitleTypography>
                    <Switch
                      onChange={e => this.handleSwitchChange('replaceTransferGates', e)}
                      value="replaceTransferGates"
                      checked={replaceTransferGates}
                    />
                  </div>
                </ExpansionPanelSummary>
              </ExpansionPanel>
              <ExpansionPanel expanded={false} classes={{ root: classes.expansionPanelRoot }}>
                <ExpansionPanelSummary>
                  <div className={classes.switchForm}>
                    <ExpansionTitleTypography>Limit Cutsets</ExpansionTitleTypography>
                    <TextField
                      type="number"
                      inputProps={{ step: 1 }}
                      label="Maximum number of cutsets"
                      name="user_defined_max_cutset"
                      value={user_defined_max_cutset}
                      onChange={this.handleChange}
                      InputLabelProps={{ shrink: true }}
                      margin="normal"
                      variant="outlined"
                    />
                  </div>
                </ExpansionPanelSummary>
              </ExpansionPanel>
            </DialogContent>
            <DialogActions>
              <Button
                fullWidth
                size={"medium"}
                variant="contained"
                onClick={() => { this.setQuantificationConfigs(); onClose(); }}
                color="primary"
                disabled={userConfigNameError || selectMissionTime && missionTimeError || selectUncertainty && samplingMethodError || selectUncertainty && sampleNumberError || selectUncertainty && confidenceLevelError}
              >
                Save
              </Button>
              <QuantifyTreeButton
                modelId={modelId}
                treeId={treeId}
                treeType={treeType}
                setConfig={this.setQuantificationConfigs}
                buttonProps={{
                  fullWidth: true,
                  size: 'medium',
                  classOverride: classes.action,
                }}
                disabled={userConfigNameError || selectMissionTime && missionTimeError || selectUncertainty && samplingMethodError || selectUncertainty && sampleNumberError || selectUncertainty && confidenceLevelError}
                quantifyOverride={quantifyOverride && quantifyOverride}
              />
            </DialogActions>
          </Dialog>
        )}
      </FullScreenConsumer>
    );
  }
}

export default withStyles(styles)(UserQuantificationSettings);
